import type { FC } from "react";
import { useMemo } from "react";

import { isEmpty } from "lodash";
import { useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { useSignalPersonaProfilesQuery } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useSignalPersonaProfilesQuery";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import SignalPersonasFilter from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalPersonasFilter/SignalPersonasFilter";
import SignalRoleChangeTypesFilter from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalRoleChangeTypesFilter/SignalRoleChangeTypesFilter";
import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import SignalBasicLabel from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalBasicLabel/SignalBasicLabel";
import { StyledDropdownLabelWrapped } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsDropdownLabel/styles";
import {
    StyledFiltersViewContent,
    StyledFiltersViewInner,
} from "app/pages/sales-intelligence/sub-modules/signals/components/styles";
import useSignalFiltersState from "app/pages/sales-intelligence/sub-modules/signals/hooks/useFiltersState";
import type IRoleChangesSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/role-changes/RoleChangesSignalFilters";
import type RoleChangeType from "app/pages/sales-intelligence/sub-modules/signals/models/role-changes/RoleChangeType";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import { selectSignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import { isKnownRoleChangeType } from "app/pages/sales-intelligence/sub-modules/signals/utils/role-changes/roleChangeTypeI18n";

import { initialiseFiltersState, reconcileEmployeeProfileIdsWithProfiles } from "./utils";

const EMPTY_PROFILES: readonly ProfileDto[] = [];

type RoleChangesSignalFiltersViewProps = SignalFiltersViewProps<IRoleChangesSignalFilters> & {
    isShared: boolean;
};

const RoleChangesSignalFiltersView: FC<RoleChangesSignalFiltersViewProps> = ({
    filters,
    renderSubmit,
    notificationView,
    onFiltersChange,
    onFiltersSubmit,
    isShared,
}) => {
    const signalsDescription = useSelector(selectSignalsDescription);
    const { data: profilesResponse } = useSignalPersonaProfilesQuery(isShared);
    const profiles = profilesResponse?.profiles ?? EMPTY_PROFILES;

    const changeTypeOptions = useMemo((): readonly RoleChangeType[] => {
        const changeTypes = signalsDescription?.roleChanges?.changeTypes ?? [];

        return changeTypes.filter((changeType): changeType is RoleChangeType => isKnownRoleChangeType(changeType));
    }, [signalsDescription?.roleChanges?.changeTypes]);

    const { currentFilters, setCurrentFilters } = useSignalFiltersState(
        initialiseFiltersState({ filters, changeTypeOptions, profiles }),
        onFiltersChange,
    );

    const changeTypes = currentFilters.changeTypes ?? [];
    const employeeProfileIds = reconcileEmployeeProfileIdsWithProfiles(currentFilters.employeeProfileIds, profiles);

    const handleFilterChange = (updatedFilters: Partial<IRoleChangesSignalFilters>): void => {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
    };

    const handleChangeTypesChange = (updatedChangeTypes: readonly RoleChangeType[]): void => {
        handleFilterChange({ changeTypes: updatedChangeTypes });
    };

    const handlePersonasChange = (updatedEmployeeProfileIds: readonly string[]): void => {
        handleFilterChange({ employeeProfileIds: updatedEmployeeProfileIds });
    };

    const handleSubmit = () => {
        onFiltersSubmit({ ...currentFilters, changeTypes, employeeProfileIds });
    };

    const isSubmitDisabled = isEmpty(changeTypes);

    return (
        <StyledFiltersViewInner
            alignItems="center"
            data-automation="role-changes-signal-filters-view"
        >
            <StyledFiltersViewContent>
                <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    gap="8px"
                >
                    <SignalBasicLabel>
                        <I18n translatable={{ key: signalsI18nKeys.roleChanges.label.showContactsWho }} />
                    </SignalBasicLabel>

                    <SignalRoleChangeTypesFilter
                        filterKey={FilterType.RoleChangeTypes}
                        selectedValue={changeTypes}
                        autoOpen={changeTypes.length === 0}
                        options={changeTypeOptions}
                        onValueChange={handleChangeTypesChange}
                    />

                    <Flex width="100%">
                        <I18n
                            element={StyledDropdownLabelWrapped}
                            translatable={{
                                key: signalsI18nKeys.roleChanges.label.matching,
                            }}
                        />

                        <SignalPersonasFilter
                            filterKey={FilterType.EmployeeProfileIds}
                            selectedValue={employeeProfileIds}
                            options={profiles}
                            onValueChange={handlePersonasChange}
                        />
                    </Flex>
                </Flex>

                <Flex
                    flexDirection="column"
                    gap="16px"
                    margin="auto 0 0 0"
                >
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon["400"]}
                    >
                        <I18n translatable={{ key: signalsI18nKeys.roleChanges.noLookbackNote }} />
                    </Text>
                    {notificationView}
                </Flex>
            </StyledFiltersViewContent>

            <StyledModalFooter>
                {renderSubmit({
                    isDisabled: isSubmitDisabled,
                    onSubmit: handleSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
};

export default RoleChangesSignalFiltersView;
